import { generationFailureMessage } from '../../utils/generationFailure';
/**
 * GlobalProgressWidget Component
 *
 * Floating progress card for bulk question generation.
 * Shows persistent progress tracking across all pages.
 *
 * Features:
 * - Appears in bottom-right corner when generation is active
 * - Real-time progress bar and counts (polled from backend every 2 seconds)
 * - Cancel button available even after page refresh (backend job queue)
 * - Link to navigate to target workbook
 * - Auto-dismisses after successful completion
 * - Persists across page navigation
 * - Survives page refresh via server-side job recovery
 *   - Progress automatically resumes from backend job status
 *   - Cancel works from any device at any time
 *   - No data loss - backend saves all generated questions immediately
 *
 * Architecture:
 * - Backend job queue processes questions asynchronously
 * - Frontend polls job status every 2 seconds
 * - Cancellation calls backend API to set job.status = 'cancelled'
 * - Multi-device support: start on laptop, cancel from phone
 *
 * Position: fixed right-4 z-40, above the support-chat button (below modals z-50)
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader2, ExternalLink, CheckCircle, AlertCircle, ChevronDown, ChevronUp, Copy, Mail, Clock, RefreshCw, ShoppingBag, Info } from 'lucide-react';
import {
  useQuestionGenerationContext,
  type ErrorDetail,
  type OneClickRetryPending,
} from '@/contexts/QuestionGenerationContext';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/contexts/ToastContext';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { generationJobsApi } from '@/services/api';
import { isInsufficientCreditError } from '@/utils/insufficientCredit';
import { useAuth } from '@/hooks/useAuth';
import { isOneClickJobKind, oneclickResultPath, oneclickLiveResultPath, oneclickPendingOcrPath } from '@/utils/oneclickJob';
import { isLiveParallelJob, type ParallelGenerationJob } from '@/contexts/useParallelGenerationJobs';
import { OneClickPointsDialog } from '@/components/oneclick/OneClickPointsDialog';
import { isOneClickCreditError } from '@/services/oneclickApi';
import { oneclickTransformNotice, oneclickUnspecifiedCountWarning } from '@/utils/oneclickPoints';
import { getApiErrorMessage } from '@/lib/utils';
import { useOcrSubmissionStore } from '@/stores/useOcrSubmissionStore';
import { OcrSubmissionProgressWidget } from '@/components/oneclick/OcrSubmissionProgressWidget';

const DEPLOYMENT_GATE_HINT = '업데이트가 끝나면 자동으로 이어집니다';
const GENERATION_CANCEL_MESSAGE = '진행중인 문제 생성이 중단됩니다. 중단하시겠습니까? (완성된 문제들은 유지됩니다.)';
/** REQ-OCB-020 (j): a charged one-click generation is billed for what was generated before the cancel. */
const ONECLICK_CANCEL_CHARGE_MESSAGE =
  '취소하면 지금까지 생성된 문항 수만큼 포인트가 청구되고, 나머지는 환급됩니다. 취소하시겠습니까?';
/** REQ-OCB-020 (c): an OCR job without a recorded page count cannot be priced for a charged retry. */
const OCR_RETRY_REUPLOAD_MESSAGE = '이 시험지 읽기는 다시 시도할 수 없습니다. 파일을 다시 올려 주세요.';

/** Retry button label: one-click jobs start over as a new charged job (REQ-OCB-022). */
function retryLabel(jobKind: string | null | undefined): string {
  if (jobKind === 'oneclick_ocr') return '다시 읽기';
  if (jobKind === 'oneclick_mock') return '다시 출제하기';
  return '남은 문제 재시도';
}

/** Tooltip of the retry button in the credit-shortage box. */
function creditRetryTitle(jobKind: string | null | undefined): string {
  return isOneClickJobKind(jobKind) ? retryLabel(jobKind) : '남은 문제 이어서 출제하기';
}

/** Points confirmation for a one-click retry (REQ-OCB-020 (c), REQ-OCB-022). */
function OneClickRetryDialog({
  pending,
  isFreeSession,
  onSubmit,
  onClose,
  onPurchase,
}: {
  pending: OneClickRetryPending | null;
  isFreeSession: boolean;
  onSubmit: (pending: OneClickRetryPending) => Promise<void>;
  onClose: () => void;
  onPurchase: () => void;
}) {
  const isOcr = pending?.kind === 'oneclick_ocr';
  const missingPages = pending?.kind === 'oneclick_ocr' && pending.pages == null;
  const unspecifiedCount = pending?.kind === 'oneclick_mock' && pending.request.question_count == null;
  const transformCount =
    pending?.kind === 'oneclick_mock' ? Object.keys(pending.request.passage_transforms ?? {}).length : 0;
  return (
    <OneClickPointsDialog
      open={pending !== null}
      title={isOcr ? '다시 읽기 예상 포인트' : '다시 출제 예상 포인트'}
      confirmLabel={isOcr ? '다시 읽기 시작' : '다시 출제'}
      points={pending?.points ?? null}
      isFreeSession={isFreeSession}
      blockReason={missingPages && !isFreeSession ? OCR_RETRY_REUPLOAD_MESSAGE : null}
      warning={unspecifiedCount ? oneclickUnspecifiedCountWarning(transformCount) : null}
      notice={oneclickTransformNotice(transformCount)}
      onConfirm={() => (pending ? onSubmit(pending) : Promise.resolve())}
      onClose={onClose}
      onPurchase={onPurchase}
    />
  );
}
const WIDGET_CONTAINER_CLASS =
  'fixed bottom-44 right-4 z-40 w-[calc(100vw-2rem)] max-w-xs animate-in slide-in-from-bottom-5 duration-300 md:bottom-24 md:w-80';

function parallelJobStatusLabel(job: ParallelGenerationJob): string {
  if (job.jobKind === 'oneclick_ocr') {
    if (job.status === 'completed') return '읽기 완료';
    if (job.status === 'failed') return '읽기 실패';
    if (job.status === 'processing') return '읽는 중';
  }
  switch (job.status) {
    case 'queued':
      return '대기 중';
    case 'completed':
      if (job.errorCount === 0) return '생성 완료';
      return job.successCount === 0 ? '생성 실패' : '일부 문제 생성 실패';
    case 'failed':
      return '생성 실패';
    case 'cancelled':
      return '생성 취소됨';
    case 'gone':
      return '저장된 결과 확인';
    default:
      return '문제 생성 중';
  }
}

function formatErrorDetails(
  errors: ErrorDetail[],
  keptErrors: ErrorDetail[],
  errorCount: number,
  includeRaw: boolean,
): string {
  const details = errors
    .map((err, idx) => {
      let text = `${idx + 1}. [${err.question_type}] ${err.passage_id}\n`;
      text += `   Error: ${includeRaw ? err.error : generationFailureMessage(err.error)}\n`;
      if (includeRaw && err.traceback) {
        text += `   Traceback:\n${err.traceback}`;
      }
      return text;
    })
    .join('\n\n' + '='.repeat(80) + '\n\n');
  const omittedErrorCount = Math.max(0, errorCount - keptErrors.length);
  if (omittedErrorCount <= 0) return details;
  const notice = `[알림] 전체 오류 ${errorCount}건 중 ${keptErrors.length}건만 보관되었습니다. `
    + `나머지 ${omittedErrorCount}건의 상세 로그는 서버 보호를 위해 생략되었습니다.`;
  return details ? `${details}\n\n${notice}` : notice;
}

/** Copy / administrator e-mail report of a job's failed items, with its dialogs. */
function useGenerationErrorReport() {
  const { showToast } = useToast();
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const copyErrors = async (errorDetails: ErrorDetail[], errorCount: number, includeRaw: boolean) => {
    // Raw exception text and tracebacks are internal diagnostics: only
    // administrators copy them; everyone else copies the user-facing guidance.
    const errorText = formatErrorDetails(errorDetails, errorDetails, errorCount, includeRaw);
    try {
      await navigator.clipboard.writeText(errorText);
      showToast('success', '오류 내용이 클립보드에 복사되었습니다');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      showToast('error', '클립보드 복사에 실패했습니다. 브라우저 권한을 확인해주세요.');
    }
  };

  const sendErrorEmail = async (
    title: string,
    jobId: string,
    errorDetails: ErrorDetail[],
    errorCount: number,
  ) => {
    try {
      setIsSendingEmail(true);
      // Only send "real" errors to admin; insufficient-credit errors are user-facing
      const errorsForEmail = errorDetails.filter((err) => !isInsufficientCreditError(err));
      // The report goes to the administrator, so it keeps the raw error text.
      const errorText = formatErrorDetails(
        errorsForEmail.length > 0 ? errorsForEmail : errorDetails,
        errorDetails,
        errorCount,
        true
      );

      await generationJobsApi.sendErrorEmail({
        workbook_title: title,
        job_id: jobId,
        error_details: errorText,
      });

      setIsSendingEmail(false);
      setShowSuccessDialog(true);
    } catch (error) {
      console.error('Failed to send error email:', error);
      setIsSendingEmail(false);
      showToast('error', '이메일 전송에 실패했습니다. 관리자에게 문의하세요.');
    }
  };

  const dialogs = (
    <>
      {/* Email Sending Loading Dialog */}
      <AlertDialog
        open={isSendingEmail}
        onClose={() => {}} // Prevent closing while sending
        title="이메일 전송 중"
        message="관리자에게 오류 내용을 보내는 중입니다. 잠시만 기다려주세요."
        showConfirmButton={false}
      />

      {/* Email Success Dialog */}
      <AlertDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title="전송 완료"
        message="전송을 완료했습니다. 빠른 시일 내로 처리 후 연락드리겠습니다."
      />
    </>
  );

  return { isSendingEmail, copyErrors, sendErrorEmail, dialogs };
}

/** "오류 상세" box: failed items (passage / type / reason) with copy and admin logs. */
function GenerationErrorDetails({
  errorCount,
  errorDetails,
  fallbackMessage,
  isAdmin,
  onCopy,
}: {
  errorCount: number;
  errorDetails: ErrorDetail[];
  fallbackMessage: string | null;
  isAdmin: boolean;
  onCopy: () => void;
}) {
  const omittedErrorCount = Math.max(0, errorCount - errorDetails.length);
  return (
    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded text-xs">
      <div className="flex items-center justify-between mb-3">
        <span className="font-semibold text-red-900">오류 상세</span>
        <div className="flex items-center gap-2">
          {errorDetails.length > 0 && (
            <button
              onClick={onCopy}
              className="flex items-center gap-1 px-2 py-1 text-red-700 hover:bg-red-100 rounded transition-colors"
              title="오류 복사"
            >
              <Copy className="h-3 w-3" />
              <span className="text-xs">복사</span>
            </button>
          )}
        </div>
      </div>

      {errorDetails.length === 0 ? (
        <div className="text-gray-600 whitespace-pre-wrap py-2">
          {generationFailureMessage(fallbackMessage)}
        </div>
      ) : (
        <div className="space-y-3 max-h-60 overflow-y-auto">
          {omittedErrorCount > 0 && (
            <div className="rounded border border-amber-300 bg-amber-50 p-2 text-amber-800">
              전체 오류 {errorCount}건 중 {errorDetails.length}건만 표시합니다. 나머지{' '}
              {omittedErrorCount}건의 상세 로그는 서버 보호를 위해 생략되었습니다.
            </div>
          )}
          {errorDetails.map((err, idx) => (
            <div key={idx} className="pb-3 border-b border-red-200 last:border-0">
              <div className="font-medium text-red-900 mb-2">
                {idx + 1}. [{err.question_type}] {err.passage_id}
              </div>
              <div className="text-red-700 text-xs mb-2 whitespace-pre-wrap">
                {generationFailureMessage(err.error)}
              </div>
              {isAdmin && err.traceback && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-red-800 hover:text-red-900 font-medium">
                    로그 보기
                  </summary>
                  <pre className="mt-2 p-2 bg-gray-900 text-green-400 rounded text-xs overflow-x-auto max-h-40 overflow-y-auto font-mono">
                    {err.traceback}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Compact rows for jobs running beside the primary card (parallel-generation account only). */
function ParallelGenerationJobList({
  jobs,
  isAdmin,
  chargesOneClickCancel,
  onCancel,
  onDismiss,
  onRetry,
}: {
  jobs: ParallelGenerationJob[];
  isAdmin: boolean;
  /** Charged session: cancelling a one-click generation bills what was generated (REQ-OCB-020 (j)). */
  chargesOneClickCancel: boolean;
  onCancel: (jobId: string) => Promise<void>;
  onDismiss: (jobId: string) => void;
  onRetry: (jobId: string) => Promise<void>;
}) {
  const navigate = useNavigate();
  const [cancelTarget, setCancelTarget] = useState<ParallelGenerationJob | null>(null);
  // "중단" pressed twice before the dialog closes still cancels once.
  const cancelConfirmedRef = useRef(false);
  const [expandedJobIds, setExpandedJobIds] = useState<ReadonlySet<string>>(() => new Set());
  const errorReport = useGenerationErrorReport();

  const toggleErrors = (jobId: string) => {
    setExpandedJobIds((previous) => {
      const next = new Set(previous);
      if (!next.delete(jobId)) next.add(jobId);
      return next;
    });
  };

  return (
    <div className="mb-2 bg-white rounded-lg shadow-xl border border-amber-200 divide-y divide-gray-100 max-h-[50vh] overflow-y-auto">
      {jobs.map((job) => {
        const isLive = isLiveParallelJob(job);
        const total = job.progress.total;
        const current = total > 0 ? Math.min(job.progress.current, total) : job.progress.current;
        const percentage = Math.min(100, Math.max(0, job.progress.percentage ?? 0));
        const isFailure = job.status === 'failed' || (job.status === 'completed' && job.errorCount > 0);
        // Same retry / error conditions as the primary card.
        const isCreditFailure = job.creditStopped || job.errorDetails.some(isInsufficientCreditError);
        const hasRealError = job.errorDetails.some((err) => !isInsufficientCreditError(err));
        const hasUnknownErrors = job.errorCount > 0 && job.errorDetails.length === 0;
        const isOneClick = isOneClickJobKind(job.jobKind);
        const canRetry = !isCreditFailure && (isOneClick
          ? job.status === 'failed'
          : job.workbookId !== null && (
            job.status === 'failed'
            || (job.status === 'completed' && (hasRealError || hasUnknownErrors))
          ));
        const showErrors = expandedJobIds.has(job.jobId);
        return (
          <div key={job.jobId} data-testid="parallel-generation-job" className="px-3 py-2 text-xs">
            <div className="flex items-center gap-2">
              {isLive ? (
                <Loader2 className="h-3.5 w-3.5 text-amber-600 animate-spin flex-shrink-0" />
              ) : isFailure ? (
                <AlertCircle className="h-3.5 w-3.5 text-red-600 flex-shrink-0" />
              ) : job.status === 'completed' ? (
                <CheckCircle className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
              ) : (
                <Info className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
              )}
              <span className="font-medium text-gray-900 truncate flex-1" title={job.workbookTitle}>
                {job.workbookTitle}
              </span>
              <span className="text-gray-600 flex-shrink-0">{parallelJobStatusLabel(job)}</span>
              <span className="text-amber-600 font-semibold flex-shrink-0">
                {current} / {total}
              </span>
              {isLive ? (
                <button
                  type="button"
                  onClick={() => {
                    cancelConfirmedRef.current = false;
                    setCancelTarget(job);
                  }}
                  className="text-gray-500 hover:text-red-600 transition-colors flex-shrink-0"
                  aria-label={`${job.workbookTitle} 생성 취소`}
                >
                  취소
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => onDismiss(job.jobId)}
                  className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                  aria-label={`${job.workbookTitle} 닫기`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
              <div className="bg-amber-600 h-1.5 rounded-full transition-all duration-300 ease-out" style={{ width: `${percentage}%` }} />
            </div>
            {job.message && <div className="mt-1 text-gray-600 whitespace-pre-line">{job.message}</div>}
            {job.errorCount > 0 && (
              <button
                type="button"
                onClick={() => toggleErrors(job.jobId)}
                className="mt-1 flex items-center gap-1 text-red-600 hover:text-red-700 transition-colors"
              >
                <AlertCircle className="h-3.5 w-3.5" />
                <span className="font-medium">오류: {job.errorCount}</span>
                {showErrors ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
            )}
            {showErrors && job.errorCount > 0 && (
              <>
                <GenerationErrorDetails
                  errorCount={job.errorCount}
                  errorDetails={job.errorDetails}
                  fallbackMessage={job.message}
                  isAdmin={isAdmin}
                  onCopy={() => errorReport.copyErrors(job.errorDetails, job.errorCount, isAdmin)}
                />
                {(hasRealError || hasUnknownErrors) && !isCreditFailure && (
                  <button
                    type="button"
                    onClick={() => errorReport.sendErrorEmail(job.workbookTitle, job.jobId, job.errorDetails, job.errorCount)}
                    disabled={errorReport.isSendingEmail}
                    className="mt-2 flex items-center gap-1 px-2 py-1 bg-amber-600 text-white hover:bg-amber-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="관리자에게 이메일 보내기"
                  >
                    <Mail className="h-3.5 w-3.5" />
                    <span className="font-medium">이메일 보내기</span>
                  </button>
                )}
              </>
            )}
            {isCreditFailure && !isLive && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                <div className="font-medium text-blue-900">포인트가 부족하여 출제가 중단되었습니다</div>
                <div className="flex gap-2 mt-1">
                  <button
                    type="button"
                    onClick={() => navigate('/store')}
                    className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white hover:bg-blue-700 rounded transition-colors"
                    title="포인트 충전하러 가기"
                  >
                    <ShoppingBag className="h-3.5 w-3.5" />
                    <span className="font-medium">포인트 충전하기</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => void onRetry(job.jobId)}
                    disabled={job.retrying}
                    className="flex items-center gap-1 px-2 py-1 bg-amber-600 text-white hover:bg-amber-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={creditRetryTitle(job.jobKind)}
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${job.retrying ? 'animate-spin' : ''}`} />
                    <span className="font-medium">재시도</span>
                  </button>
                </div>
              </div>
            )}
            {canRetry && (
              <div className="mt-2">
                <p className="mb-1 text-gray-600">
                  {isOneClick
                    ? job.jobKind === 'oneclick_ocr' ? '올린 시험지를 다시 읽습니다.' : '같은 샘플과 지문으로 다시 출제합니다.'
                    : '완료된 문제는 유지하고, 실패하거나 처리되지 않은 문제만 같은 설정으로 이어서 출제합니다.'}
                </p>
                <Button
                  type="button"
                  onClick={() => void onRetry(job.jobId)}
                  disabled={job.retrying}
                  className="w-full flex items-center justify-center gap-1 text-xs py-1"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${job.retrying ? 'animate-spin' : ''}`} />
                  {job.retrying ? '재시도 준비 중...' : retryLabel(job.jobKind)}
                </Button>
              </div>
            )}
            {job.jobKind === 'oneclick_mock' && ['queued', 'processing', 'orphaned'].includes(job.status) && (
              <button
                type="button"
                onClick={() => navigate(oneclickLiveResultPath(job.jobId))}
                className="mt-1 flex items-center gap-1 text-amber-700 hover:text-amber-800 font-medium"
              >
                출제 결과 보기
                <ExternalLink className="h-3 w-3" />
              </button>
            )}
            {job.status === 'completed' && (job.resultDraftId != null || job.jobKind === 'oneclick_ocr') && (
              <button
                type="button"
                onClick={() => navigate(job.resultDraftId != null
                  ? oneclickResultPath(job.resultDraftId, job.jobKind)
                  : oneclickPendingOcrPath(job.jobId))}
                className="mt-1 flex items-center gap-1 text-amber-700 hover:text-amber-800 font-medium"
              >
                결과 보기
                <ExternalLink className="h-3 w-3" />
              </button>
            )}
            {job.workbookId !== null && (
              <button
                type="button"
                onClick={() => navigate(`/workbook/${job.workbookId}`)}
                className="mt-1 flex items-center gap-1 text-amber-700 hover:text-amber-800 font-medium"
              >
                교재로 이동
                <ExternalLink className="h-3 w-3" />
              </button>
            )}
          </div>
        );
      })}
      <AlertDialog
        open={cancelTarget !== null}
        onClose={() => setCancelTarget(null)}
        title="문제 생성 중단"
        message={
          chargesOneClickCancel && cancelTarget?.jobKind === 'oneclick_mock'
            ? ONECLICK_CANCEL_CHARGE_MESSAGE
            : GENERATION_CANCEL_MESSAGE
        }
        confirmText="중단"
        cancelText="계속 진행"
        onConfirm={() => {
          const target = cancelTarget;
          setCancelTarget(null);
          if (!target || cancelConfirmedRef.current) return;
          cancelConfirmedRef.current = true;
          void onCancel(target.jobId);
        }}
      />
      {errorReport.dialogs}
    </div>
  );
}

export function GlobalProgressWidget() {
  const submission = useOcrSubmissionStore(state => state.submission);
  return <>
    {submission && <OcrSubmissionProgressWidget submission={submission} />}
    <div hidden={submission !== null}><GenerationProgressWidget /></div>
  </>;
}

function GenerationProgressWidget() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user, isImpersonating } = useAuth();
  const isAdmin = user?.role === 'administrator';
  // Display only (REQ-OCB-020 (f)(k)): administrators and impersonation sessions are not charged.
  const isFreeSession = isAdmin || Boolean(isImpersonating);
  const {
    isGenerating,
    progress,
    isCompletionReady,
    workbookId,
    workbookTitle,
    jobId,
    jobKind,
    resultDraftId,
    isQueued,
    queuePosition,
    queueMessage,
    queueReason,
    successCount,
    errorCount,
    errorDetails, // Use cached error details from context
    pollingError, // API error message (e.g., 404)
    pollingErrorType, // Structured error type: 'reconnecting' | 'fatal' | 'insufficient_credits' | 'stall_soft' | 'stall_hard' | null
    cancelGeneration,
    retryPolling,
    retryFailedGeneration,
    submitOneClickRetry,
    parallelJobs = [],
    cancelParallelJob,
    dismissParallelJob,
    retryParallelJob,
  } = useQuestionGenerationContext();

  // Stall hints (SPEC-STALL-HEARTBEAT-001 REQ-UNW-003) are passive "please wait" notices
  // while the job is still processing — they must never read or behave like a failure.
  const isStallHint = pollingErrorType === 'stall_soft' || pollingErrorType === 'stall_hard' || pollingErrorType === 'reconnecting';
  // Deployment gate: the job is parked until the server update finishes, so the
  // generic "starts after the job ahead of you" line would be misleading.
  const showDeploymentGateHint =
    queueReason === 'deployment_gate' && !(queueMessage ?? '').includes(DEPLOYMENT_GATE_HINT);
  // A server queue explanation (e.g. deployment resume on an orphaned job) always
  // renders in this neutral box, never in the error banner.
  const showQueueBox = (isQueued || Boolean(queueMessage)) && !pollingError;
  const queueSecondaryLine = showDeploymentGateHint
    ? DEPLOYMENT_GATE_HINT
    : queueReason === 'deployment_gate' || queueReason === 'deployment_resume'
      ? null
      : isQueued
        ? '앞선 작업이 완료되면 자동으로 시작됩니다.'
        : null;
  // Terminal informational states: the job row is gone or was cancelled elsewhere.
  // Nothing is running, so closing needs no confirmation and there is nothing to cancel.
  const isNoticeState = pollingErrorType === 'notice' || pollingErrorType === 'cancelled';
  const isBlockingError = Boolean(pollingError) && !isStallHint && !isNoticeState;

  const [showErrors, setShowErrors] = useState(false);
  const errorReport = useGenerationErrorReport();
  const isSendingEmail = errorReport.isSendingEmail;
  const [isRetryingFailedGeneration, setIsRetryingFailedGeneration] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelMessage, setCancelMessage] = useState('');
  // "중단" pressed twice (or click + Enter) before the dialog closes still cancels once.
  const cancelConfirmedRef = useRef(false);
  const cancelActionRef = useRef('widget_cancel_confirmed');
  // One-click retry waiting for its points confirmation, and its request failure.
  const [retryPending, setRetryPending] = useState<OneClickRetryPending | null>(null);
  const [retryError, setRetryError] = useState<{ message: string; credit: boolean } | null>(null);

  // Capture jobId before finishGeneration clears it (needed for email error reporting)
  const lastJobIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (jobId) {
      lastJobIdRef.current = jobId;
    }
  }, [jobId]);

  const handleRetryParallelJob = async (retryJobId: string) => {
    setRetryError(null);
    const pending = await retryParallelJob(retryJobId);
    if (pending) setRetryPending(pending);
  };

  const handleSubmitOneClickRetry = async (pending: OneClickRetryPending) => {
    try {
      await submitOneClickRetry(pending);
    } catch (error) {
      setRetryError({
        message: getApiErrorMessage(error, '재시도에 실패했습니다.'),
        credit: isOneClickCreditError(error),
      });
    }
  };

  const oneClickRetryDialog = (
    <OneClickRetryDialog
      pending={retryPending}
      isFreeSession={isFreeSession}
      onSubmit={handleSubmitOneClickRetry}
      onClose={() => setRetryPending(null)}
      onPurchase={() => navigate('/store')}
    />
  );

  const oneClickRetryError = retryError ? (
    <div role="alert" className="mb-2 p-3 bg-red-50 border border-red-300 rounded-lg text-xs text-red-700">
      <div className="whitespace-pre-line">{retryError.message}</div>
      {retryError.credit && (
        <button
          type="button"
          onClick={() => navigate('/store')}
          className="mt-2 flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 rounded transition-colors"
        >
          <ShoppingBag className="h-3.5 w-3.5" />
          <span className="text-xs font-medium">포인트 충전하기</span>
        </button>
      )}
    </div>
  ) : null;

  const parallelJobList = parallelJobs.length > 0 ? (
    <ParallelGenerationJobList
      jobs={parallelJobs}
      isAdmin={isAdmin}
      chargesOneClickCancel={!isFreeSession}
      onCancel={cancelParallelJob}
      onDismiss={dismissParallelJob}
      onRetry={handleRetryParallelJob}
    />
  ) : null;

  // Don't render if not generating
  if (!isGenerating) {
    return parallelJobList || retryError || retryPending ? (
      <div className={WIDGET_CONTAINER_CLASS}>
        {oneClickRetryError}
        {parallelJobList}
        {oneClickRetryDialog}
      </div>
    ) : null;
  }

  const handleViewWorkbook = () => {
    if (workbookId) {
      navigate(`/workbook/${workbookId}`);
    }
  };

  const handleViewOneClickResult = () => {
    if (resultDraftId != null) {
      navigate(oneclickResultPath(resultDraftId, jobKind));
    } else if (jobKind === 'oneclick_ocr' && jobId) {
      navigate(oneclickPendingOcrPath(jobId));
    }
  };

  const handleToggleErrors = () => {
    // Error details are already cached in context, no need to fetch
    setShowErrors(!showErrors);
  };

  const handleCopyErrors = () => errorReport.copyErrors(errorDetails, errorCount, isAdmin);

  const handleSendEmail = async () => {
    // Use captured jobId (finishGeneration clears jobId when errors exist, but we need it for email)
    const effectiveJobId = jobId || lastJobIdRef.current;

    if (!effectiveJobId) {
      showToast('error', '작업 ID를 찾을 수 없습니다. 오류 내용을 복사하여 관리자에게 직접 전달해주세요.');
      return;
    }

    await errorReport.sendErrorEmail(workbookTitle || '문제 생성', effectiveJobId, errorDetails, errorCount);
  };

  const handleCancelClick = (isXButton: boolean = false) => {
    // If completion already reached terminal UI state, just close without confirmation.
    const isTerminalState = isSyncingCompletion || isComplete || isNoticeState;

    // When an error already stopped the job, close immediately without confirmation.
    // Stall hints are not errors — the job is still running, so confirm like normal progress.
    // 'fatal' only means this browser lost the server; the job may still be running,
    // so it gets the same confirmation as normal progress.
    if ((isBlockingError && pollingErrorType !== 'fatal') || isTerminalState) {
      cancelGeneration('widget_dismiss_terminal');
    } else {
      // Show confirmation dialog with appropriate message
      cancelConfirmedRef.current = false;
      cancelActionRef.current = isXButton ? 'widget_close_confirmed' : 'widget_cancel_confirmed';
      if (jobKind === 'oneclick_ocr') {
        setCancelMessage('진행 중인 시험지 읽기를 중단하시겠습니까?');
      } else if (jobKind === 'oneclick_mock' && !isFreeSession) {
        // REQ-OCB-020 (j): the cancel API bills what was generated; say so first.
        setCancelMessage(ONECLICK_CANCEL_CHARGE_MESSAGE);
      } else if (isXButton) {
        setCancelMessage('이 창을 닫으면 진행중인 문제 생성이 중단됩니다. 중단하시겠습니까? (완성된 문제들은 유지됩니다.)');
      } else {
        setCancelMessage(GENERATION_CANCEL_MESSAGE);
      }
      setShowCancelConfirm(true);
    }
  };

  const handleConfirmCancel = async () => {
    setShowCancelConfirm(false);
    if (cancelConfirmedRef.current) return;
    cancelConfirmedRef.current = true;
    await cancelGeneration(cancelActionRef.current);
  };

  const creditErrorDetails = errorDetails.filter(isInsufficientCreditError);
  const realErrorDetails = errorDetails.filter((err) => !isInsufficientCreditError(err));
  const isCreditFailure = creditErrorDetails.length > 0 || pollingErrorType === 'insufficient_credits';
  const hasRealError = realErrorDetails.length > 0;
  // When error details haven't loaded yet, fall back to treating unknown errors as real
  const hasUnknownErrors = errorCount > 0 && errorDetails.length === 0;

  // The worker can overshoot (completed > total); treat it as reached and clamp the display.
  const hasReachedBackendTotal = progress.total > 0 && progress.current >= progress.total;
  const displayCurrent = progress.total > 0 ? Math.min(progress.current, progress.total) : progress.current;
  const displayPercentage = Math.min(100, Math.max(0, progress.percentage ?? 0));
  const isSyncingCompletion = hasReachedBackendTotal && !isCompletionReady && !isBlockingError;
  const isComplete = hasReachedBackendTotal && isCompletionReady;
  // A clean completion (no errors) must win over a stale "reconnecting" banner:
  // a job that finished while the tab was frozen should read as done, not stuck.
  // Errors (real, unknown-pending, or credit) still take the error UI path.
  const showCleanComplete = isComplete && !hasRealError && !hasUnknownErrors && !isCreditFailure;
  // A completion with errors, unknown errors or a credit stop is not a success.
  const showFailedComplete = isComplete && !showCleanComplete;
  const failedCompleteTitle = successCount === 0 ? '생성 실패' : '일부 문제 생성 실패';

  // Hide workbook link for Single Question page (uses temporary workbooks)
  const isSingleQuestionMode = workbookTitle === '한 문제 제작';
  const showWorkbookLink = workbookId && !isSingleQuestionMode;
  // One-click mock exam: the destination is the saved result, not a workbook.
  const isOneClickJob = isOneClickJobKind(jobKind);
  const showOneClickResultLink = isOneClickJob && isComplete &&
    (resultDraftId != null || (jobKind === 'oneclick_ocr' && !!jobId));

  const canRetryWorkbook = Boolean(jobId && workbookId && !isCreditFailure && (
    pollingErrorType === 'generation_failed' || (isComplete && (hasRealError || hasUnknownErrors))
  ));
  const canRetryOneClick = Boolean(
    jobId && isOneClickJob && !isCreditFailure && pollingErrorType === 'generation_failed'
  );
  const canRetryFailedGeneration = canRetryWorkbook || canRetryOneClick;

  const handleRetryFailedGeneration = async () => {
    setRetryError(null);
    try {
      setIsRetryingFailedGeneration(true);
      // A one-click job comes back as a pending request to confirm (REQ-OCB-022).
      const pending = await retryFailedGeneration();
      if (pending) setRetryPending(pending);
    } finally {
      setIsRetryingFailedGeneration(false);
    }
  };

  return (
    <div className={WIDGET_CONTAINER_CLASS}>
      {oneClickRetryError}
      {parallelJobList}
      <div className="bg-white rounded-lg shadow-2xl border-2 border-amber-200 w-full max-h-[calc(100vh-12rem)] overflow-y-auto md:max-h-none">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-amber-50">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2 flex-1 min-w-0">
              {showCleanComplete ? (
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : pollingError && (pollingErrorType === 'reconnecting' || pollingErrorType === 'stall_hard') ? (
                <RefreshCw className="h-5 w-5 text-amber-600 animate-spin flex-shrink-0 mt-0.5" />
              ) : isNoticeState ? (
                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              ) : isBlockingError ? (
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              ) : showFailedComplete ? (
                <AlertCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${successCount === 0 ? 'text-red-600' : 'text-amber-600'}`} />
              ) : isComplete ? (
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : isSyncingCompletion ? (
                <Loader2 className="h-5 w-5 text-blue-600 animate-spin flex-shrink-0 mt-0.5" />
              ) : isQueued ? (
                <Clock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              ) : (
                <Loader2 className="h-5 w-5 text-amber-600 animate-spin flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 truncate">
                  {showCleanComplete
                    ? jobKind === 'oneclick_ocr' ? '읽기 완료' : '생성 완료'
                    : pollingError && pollingErrorType === 'reconnecting'
                    ? '서버 재연결 중'
                    : pollingError && pollingErrorType === 'stall_hard'
                    ? '서버 응답 확인 중'
                    : pollingErrorType === 'notice'
                    ? '저장된 결과 확인'
                    : pollingErrorType === 'cancelled'
                    ? '생성 취소됨'
                    : isBlockingError
                    ? jobKind === 'oneclick_ocr' ? '읽기 실패' : '문제 생성 실패'
                    : showFailedComplete
                    ? failedCompleteTitle
                    : isComplete
                      ? '생성 완료'
                      : isSyncingCompletion
                        ? '결과 반영 중'
                        : isQueued
                          ? '대기 중'
                          : jobKind === 'oneclick_ocr' ? '시험지 읽기 진행 중' : '문제 생성 중'}
                </h3>
                {workbookTitle && (
                  <p className="text-xs text-gray-600 truncate" title={workbookTitle}>
                    {workbookTitle}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => handleCancelClick(true)}
              className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              title="닫기"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* API Error / Reconnection / Stall Hint Alert */}
          {pollingError && !showCleanComplete && (
            <div className={`mb-3 p-3 rounded-lg border ${
              pollingErrorType === 'reconnecting' || pollingErrorType === 'stall_hard'
                ? 'bg-amber-50 border-amber-300'
                : (pollingErrorType === 'stall_soft' || isNoticeState)
                  ? 'bg-blue-50 border-blue-200'
                  : 'bg-red-50 border-red-300'
            }`}>
              <div className="flex items-start gap-2">
                {pollingErrorType === 'reconnecting' || pollingErrorType === 'stall_hard' ? (
                  <RefreshCw className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5 animate-spin" />
                ) : (pollingErrorType === 'stall_soft' || isNoticeState) ? (
                  <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                ) : isCreditFailure ? (
                  <ShoppingBag className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <div className={`text-sm font-semibold mb-1 ${
                    pollingErrorType === 'reconnecting' || pollingErrorType === 'stall_hard'
                      ? 'text-amber-900'
                      : (pollingErrorType === 'stall_soft' || isNoticeState)
                        ? 'text-blue-900'
                        : 'text-red-900'
                  }`}>
                    {pollingErrorType === 'reconnecting'
                      ? '서버 재연결 중'
                      : pollingErrorType === 'notice'
                        ? '저장된 결과 확인'
                      : pollingErrorType === 'cancelled'
                        ? '생성 취소됨'
                      : pollingErrorType === 'stall_soft'
                        ? '조금 더 걸리고 있어요'
                        : pollingErrorType === 'stall_hard'
                          ? '서버 응답 지연'
                          : isCreditFailure
                            ? '포인트 부족'
                            : pollingErrorType === 'generation_failed'
                              ? '생성 실패'
                              : 'API 오류'}
                  </div>
                  <div className={`text-xs whitespace-pre-line ${
                    pollingErrorType === 'reconnecting' || pollingErrorType === 'stall_hard'
                      ? 'text-amber-700'
                      : (pollingErrorType === 'stall_soft' || isNoticeState)
                        ? 'text-blue-700'
                        : 'text-red-700'
                  }`}>
                    {pollingErrorType === 'generation_failed' ? generationFailureMessage(pollingError) : pollingError}
                  </div>
                  {/* Retry button for final give-up state (not during active reconnection) */}
                  {jobId && pollingErrorType === 'fatal' && (
                    <button
                      onClick={retryPolling}
                      className="mt-2 flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white hover:bg-amber-700 rounded transition-colors text-xs font-medium"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      재시도
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Queue Status Info */}
          {showQueueBox && (
            <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Clock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-blue-900 mb-1">
                    {queueMessage || '다른 작업이 진행 중입니다'}
                  </div>
                  {(isQueued || queueSecondaryLine) && (
                    <div className="text-xs text-blue-700">
                      {isQueued && queuePosition > 0 && (
                        <>
                          현재 대기 순서: {queuePosition}번째
                          <br />
                        </>
                      )}
                      {queueSecondaryLine}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Progress Text */}
          <div className="mb-3">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-gray-700 font-medium">
                진행 상황
              </span>
              <span className="text-amber-600 font-bold">
                {displayCurrent} / {progress.total}
              </span>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
              <div
                className="bg-amber-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${displayPercentage}%` }}
              />
            </div>

            <div className="text-xs text-gray-500 mt-1 text-right">
              {displayPercentage}% 완료
            </div>
          </div>

          {/* Results Summary */}
          {(successCount > 0 || errorCount > 0) && (
            <div className="mb-3">
              <div className="flex items-center gap-4 text-xs p-2 bg-gray-50 rounded">
                {successCount > 0 && (
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="h-3.5 w-3.5" />
                    <span className="font-medium">성공: {successCount}</span>
                  </div>
                )}
                {errorCount > 0 && (
                  <button
                    onClick={handleToggleErrors}
                    className="flex items-center gap-1 text-red-600 hover:text-red-700 transition-colors"
                  >
                    <AlertCircle className="h-3.5 w-3.5" />
                    <span className="font-medium">오류: {errorCount}</span>
                    {showErrors ? (
                      <ChevronUp className="h-3 w-3" />
                    ) : (
                      <ChevronDown className="h-3 w-3" />
                    )}
                  </button>
                )}
              </div>

              {/* Error Details Dropdown */}
              {showErrors && errorCount > 0 && (
                <GenerationErrorDetails
                  errorCount={errorCount}
                  errorDetails={errorDetails}
                  fallbackMessage={pollingError}
                  isAdmin={isAdmin}
                  onCopy={handleCopyErrors}
                />
              )}

              {/* Insufficient Credit Notice */}
              {isCreditFailure && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded text-xs">
                  <div className="text-center mb-2">
                    <span className="font-medium text-blue-900">
                      포인트가 부족하여 출제가 중단되었습니다
                    </span>
                    <div className="text-blue-700 mt-1">
                      포인트 충전 후 재시도하면 남은 문제만 이어서 출제됩니다.
                    </div>
                  </div>
                  <div className="flex justify-center gap-2">
                    <button
                      onClick={() => navigate('/store')}
                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 rounded transition-colors"
                      title="포인트 충전하러 가기"
                    >
                      <ShoppingBag className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">포인트 충전하기</span>
                    </button>
                    {jobId && (
                      <button
                        onClick={handleRetryFailedGeneration}
                        disabled={isRetryingFailedGeneration}
                        className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white hover:bg-amber-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={creditRetryTitle(jobKind)}
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${isRetryingFailedGeneration ? 'animate-spin' : ''}`} />
                        <span className="text-xs font-medium">재시도</span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Real Error Warning (only for non-credit errors) */}
              {((hasRealError || hasUnknownErrors) && !isCreditFailure) && (
                <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded text-xs">
                  <div className="text-center mb-2">
                    <span className="font-medium text-amber-800">
                      ⚠️ 관리자에게 오류 내용을 보내주세요
                    </span>
                  </div>
                  <div className="flex justify-center">
                    <button
                      onClick={handleSendEmail}
                      disabled={isSendingEmail}
                      className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white hover:bg-amber-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="관리자에게 이메일 보내기"
                    >
                      <Mail className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">이메일 보내기</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {canRetryFailedGeneration && (
            <div className="mb-3">
              <p className="mb-2 text-xs text-gray-600">
                {isOneClickJob
                  ? jobKind === 'oneclick_ocr' ? '올린 시험지를 다시 읽습니다.' : '같은 샘플과 지문으로 다시 출제합니다.'
                  : '완료된 문제는 유지하고, 실패하거나 처리되지 않은 문제만 같은 설정으로 이어서 출제합니다.'}
              </p>
              <Button
                type="button"
                onClick={handleRetryFailedGeneration}
                disabled={isRetryingFailedGeneration}
                className="w-full flex items-center justify-center gap-1"
              >
                <RefreshCw className={`h-4 w-4 ${isRetryingFailedGeneration ? 'animate-spin' : ''}`} />
                {isRetryingFailedGeneration ? '재시도 준비 중...' : retryLabel(jobKind)}
              </Button>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2">
            {/* Cancel button - always available when generating (backend job queue) */}
            {!isCreditFailure && !canRetryFailedGeneration && !isComplete && !isSyncingCompletion && !isNoticeState && jobId && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleCancelClick()}
                className="h-8 text-xs"
              >
                {jobKind === 'oneclick_ocr' ? '읽기 취소' : '생성 취소'}
              </Button>
            )}
            {showWorkbookLink && (
              <Button
                type="button"
                onClick={handleViewWorkbook}
                className="flex-1 text-xs py-1.5 flex items-center justify-center gap-1"
              >
                교재로 이동
                <ExternalLink className="h-3 w-3" />
              </Button>
            )}
            {jobKind === 'oneclick_mock' && !isComplete && jobId && (
              <Button type="button" size="sm" onClick={() => navigate(oneclickLiveResultPath(jobId))}
                className="h-8 gap-1 text-xs">
                출제 결과 보기
                <ExternalLink className="h-3 w-3" />
              </Button>
            )}
            {showOneClickResultLink && (
              <Button
                type="button"
                onClick={handleViewOneClickResult}
                className="flex-1 text-xs py-1.5 flex items-center justify-center gap-1"
              >
                결과 보기
                <ExternalLink className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>

        {/* Completion Message */}
        {isComplete && (
          <div className="px-4 pb-4">
            <div className="text-xs text-center text-green-600 font-medium">
              {successCount > 0 && (isOneClickJob
                ? jobKind === 'oneclick_ocr'
                  ? resultDraftId != null ? '읽은 내용이 저장되었습니다' : '읽은 내용을 확인하고 편집해 저장하세요'
                  : '출제 결과가 저장되었습니다'
                : `${successCount}개 문제가 생성되었습니다`)}
            </div>
            <div className="text-xs text-center text-gray-500 mt-1">
              {errorCount > 0
                ? 'X 버튼을 눌러 닫을 수 있습니다'
                : '5초 후 자동으로 닫힙니다'}
            </div>
          </div>
        )}
      </div>

      {errorReport.dialogs}
      {oneClickRetryDialog}

      {/* Cancel Confirmation Dialog */}
      <AlertDialog
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        title={jobKind === 'oneclick_ocr' ? '시험지 읽기 중단' : '문제 생성 중단'}
        message={cancelMessage}
        confirmText="중단"
        cancelText="계속 진행"
        onConfirm={handleConfirmCancel}
      />
    </div>
  );
}
